// src/components/DiversDirectory.js
// Annuaire des plongeurs : filtres canton / brevet + recherche par nom.
// Chaque carte mène au profil public. Alimenté par GET /divers.
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchWithAuth } from "../utils/api";
import { certLabel } from "../utils/certs";
import "./DiversDirectory.css";

const CANTONS = ["AG", "AI", "AR", "BE", "BL", "BS", "FR", "GE", "GL", "GR", "JU", "LU", "NE", "NW", "OW", "SG", "SH", "SO", "SZ", "TG", "TI", "UR", "VD", "VS", "ZG", "ZH"];
const CERT_OPTIONS = [
    "open_water", "advanced_open_water", "rescue", "divemaster", "instructor",
    "nitrox", "advanced_nitrox", "deep", "ccr", "trimix_normo", "trimix_hypo", "cavern", "intro_cave", "full_cave",
];

export default function DiversDirectory({ apiUrl, t, initialCert = "" }) {
    const [canton, setCanton] = useState("");
    const [cert, setCert] = useState(initialCert);
    const [q, setQ] = useState("");
    const [divers, setDivers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Si on arrive via une pastille de brevet (deep-link), applique le filtre.
    useEffect(() => { if (initialCert) setCert(initialCert); }, [initialCert]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (canton) params.set("canton", canton);
            if (cert) params.set("cert", cert);
            if (q.trim()) params.set("q", q.trim());
            const res = await fetchWithAuth(`${apiUrl}/divers?${params.toString()}`);
            if (res.ok) setDivers(await res.json());
        } catch (err) {
            console.error("Erreur chargement annuaire:", err);
        } finally {
            setLoading(false);
        }
    }, [apiUrl, canton, cert, q]);

    // Débounce léger sur la recherche texte ; immédiat sur les filtres.
    useEffect(() => {
        const id = setTimeout(load, q ? 300 : 0);
        return () => clearTimeout(id);
    }, [load, q]);

    const reset = () => { setCanton(""); setCert(""); setQ(""); };
    const hasFilter = canton || cert || q.trim();

    return (
        <div className="dir">
            <div className="dir-filters">
                <input
                    className="dir-search"
                    type="text"
                    placeholder={t("directory.searchPlaceholder")}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
                <div className="dir-selects">
                    <select className="dir-select" value={canton} onChange={(e) => setCanton(e.target.value)}>
                        <option value="">{t("directory.allCantons")}</option>
                        {CANTONS.map((c) => <option key={c} value={c}>{t(`cantons.${c}`)}</option>)}
                    </select>
                    <select className="dir-select" value={cert} onChange={(e) => setCert(e.target.value)}>
                        <option value="">{t("directory.allCerts")}</option>
                        {CERT_OPTIONS.map((c) => <option key={c} value={c}>{certLabel(t, c)}</option>)}
                    </select>
                    {hasFilter && (
                        <button type="button" className="dir-reset" onClick={reset}>{t("directory.reset")}</button>
                    )}
                </div>
            </div>

            {loading ? (
                <p className="dir-empty">{t("directory.loading")}</p>
            ) : divers.length === 0 ? (
                <p className="dir-empty">{t("directory.empty")}</p>
            ) : (
                <>
                    <p className="dir-count">{t("directory.count", { count: divers.length })}</p>
                    <div className="dir-list">
                        {divers.map((d) => (
                            <Link key={d.id} to={`/profile/${d.id}`} className="dir-card">
                                <img className="dir-av" src={d.profile_image_url || "/default-avatar.png"} alt="" />
                                <div className="dir-info">
                                    <div className="dir-name">
                                        {d.first_name} {d.last_name}
                                        {d.canton && <span className="dir-canton">{d.canton}</span>}
                                    </div>
                                    {d.certifications?.length > 0 && (
                                        <div className="dir-certs">
                                            {d.certifications.slice(0, 4).map((c) => (
                                                <span key={c} className="dir-cert">{certLabel(t, c)}</span>
                                            ))}
                                            {d.certifications.length > 4 && <span className="dir-cert dir-cert-more">+{d.certifications.length - 4}</span>}
                                        </div>
                                    )}
                                </div>
                            </Link>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
